/**
 * Homepage section registry.
 *
 * The homepage used to be a fixed JSX sequence in HomePage.tsx — order and
 * visibility both compiled in, with no way for an operator to change either.
 * Now the CMS owns that: `cmsHomeSections` (generated from
 * /api/cms/public/home-sections) gives the order and the visible subset, and
 * this file is the map from a section key to the component that renders it.
 *
 * Two rules keep the two sides honest:
 *
 *  1. This file is the source of truth for WHICH keys exist. The CMS table
 *     deliberately has no CHECK constraint on `section_key` — adding a section
 *     must not require a migration — so a row for an unknown key is skipped
 *     at render time with a console warning rather than crashing the homepage.
 *
 *  2. `defaults` must reproduce today's compiled-in behaviour exactly. With
 *     `settings = {}` (a freshly seeded row, or an operator who never opened
 *     the settings panel) the homepage has to look byte-identical to how it
 *     looked before this existed. That is what makes the migration a no-op for
 *     visitors.
 *
 * Field descriptors are declarative rather than zod schemas: the admin has to
 * *render* these, not just validate them, so a descriptor is the shape that
 * actually serves. `coerceSettings` is the single place settings are cleaned,
 * and both the admin form and the runtime read go through it.
 */

import type { ComponentType } from 'react'
import HeroSlider from '@/components/home/HeroSlider'
import AboutSnippet from '@/components/home/AboutSnippet'
import ProductsGrid from '@/components/home/ProductsGrid'
import HarbourTeaser from '@/components/home/HarbourTeaser'
import SmartCindyTeaser from '@/components/home/SmartCindyTeaser'
import SupportProcess from '@/components/about/SupportProcess'
import CtaBanner from '@/components/home/CtaBanner'
import MagazineFeature from '@/components/home/MagazineFeature'
import NewsTeaser from '@/components/home/NewsTeaser'
import Testimonials from '@/components/home/Testimonials'
import TeamGrid from '@/components/home/TeamGrid'
import LecturesFeature from '@/components/home/LecturesFeature'
import { products } from '@/content/products'
import { MAGAZINES } from '@/content/magazines'
import type { CmsHomeSection } from '@/content/nav-types'

export type SectionSettings = Record<string, unknown>

export type SectionField =
  | { kind: 'number'; key: string; label: string; min: number; max: number; step?: number; help?: string }
  | { kind: 'toggle'; key: string; label: string; help?: string }
  | {
      kind: 'select'
      key: string
      label: string
      /** `''` means "no filter". */
      options: Array<{ value: string; label: string }>
      help?: string
    }
  | {
      kind: 'multiselect'
      key: string
      label: string
      options: Array<{ value: string; label: string }>
      /** Rendered as checkboxes when short, a multi-select otherwise. */
      help?: string
    }

export interface SectionDef {
  key: string
  /** Operator-facing name, shown in the admin list. Admin chrome is hardcoded
   *  Chinese throughout, so this stays untranslated on purpose. */
  label: string
  description: string
  Component: ComponentType<{ settings?: SectionSettings }>
  fields: SectionField[]
  defaults: SectionSettings
  /** Sections that are wired up but deliberately off. Shown in the admin with
   *  a note so an operator knows they exist and why they are off. */
  note?: string
  /** When false, the section is hidden from the homepage entirely
   *  (resolveHomeSections + HomePage's registry fallback both filter
   *  it out). Used to ship a section "ready but off by default" without
   *  removing the component or breaking the admin chrome. Defaults to
   *  true when omitted. */
  enabled?: boolean
}

export const SECTION_REGISTRY: Record<string, SectionDef> = {
  hero: {
    key: 'hero',
    label: '主視覺輪播',
    description: '頁面最上方的輪播大圖。',
    Component: HeroSlider,
    // 2026-11-04: the hero has NO settings any more. It used to carry a
    // `slides` count, capped at 6 because that was how many sets of
    // `home.hero.slide<n>.*` i18n keys existed. The slides are CMS rows
    // now (`cms_hero_slides`) and their number, order and visibility are
    // edited in the hero panel on /admin/cms/pages/home — so there is
    // nothing left for a section setting to do, and leaving the field in
    // place would show an operator a control that silently does nothing.
    defaults: {},
    fields: [],
  },
  about: {
    key: 'about',
    label: '關於我們摘要',
    description: '機構簡介段落 + 核心價值圖。',
    Component: AboutSnippet,
    // 2026-10-15: section hidden per product feedback. The component
    // stays in the registry so the admin chrome still lists the toggle,
    // and the same `enabled: true` re-enables it without a code change.
    enabled: false,
    defaults: {},
    fields: [],
  },
  products: {
    key: 'products',
    label: '教育產品',
    description: '產品／服務卡片格。',
    Component: ProductsGrid,
    defaults: { slugs: [] },
    fields: [
      {
        kind: 'multiselect',
        key: 'slugs',
        label: '顯示哪些產品',
        options: products.map((p) => ({ value: p.id, label: p.titleKey.split('.').pop() ?? p.id })),
        help: '不選＝全部顯示（與原本行為相同）。',
      },
    ],
  },
  // SmartCindy comes BEFORE harbour: the operator ordered the two product
  // spotlights that way in the CMS (sort_order 40 vs 50), and this registry
  // order is the fallback used when the CMS list is empty or partial. The two
  // must agree, or the homepage would render a different order from the one
  // the admin shows. See resolveHomeSections() for why sort_order wins.
  smartcindy: {
    key: 'smartcindy',
    label: 'SmartCindy 專區',
    description: '連到 /products/smart-cindy 的重點推廣區。',
    Component: SmartCindyTeaser,
    defaults: {},
    fields: [],
  },
  harbour: {
    key: 'harbour',
    label: 'My Harbour 專區',
    description: '連到 /products/harbour 的重點推廣區。',
    Component: HarbourTeaser,
    defaults: {},
    fields: [],
  },
  process: {
    key: 'process',
    label: '支援流程',
    description: '三步驟流程說明。',
    Component: SupportProcess,
    // 2026-11-04: 先搬到產品頁（/products），同日再搬到關於我們頁
    // （/about）——「我們如何與學校同行」講的是機構與學校的關係,不是
    // 產品,放在關於我們頁才讀得通。
    // 元件與 registry 都留著,後台的「首頁 sections」仍會列出這一列,
    // 方便日後搬回首頁。要搬回去:把 enabled 改回 true,並把 CMS 那列
    // 的 is_visible 打開（見 scripts/hide-moved-home-sections.mts 的說明）。
    enabled: false,
    note: '2026-11-04 已搬到關於我們頁(/about)。首頁不再顯示這個區塊;要搬回首頁需要改程式碼,不只是打開開關。',
    defaults: {},
    fields: [],
  },
  cta: {
    key: 'cta',
    label: '行動呼籲橫幅',
    description: '頁面中段的聯絡 CTA。',
    Component: CtaBanner,
    // 2026-11-04: 搬到產品頁（/products），首頁不再渲染這個橫幅。
    // 跟 process 一樣,元件留在 registry 裡,搬回去只要改 enabled。
    enabled: false,
    note: '2026-11-04 已搬到產品頁(/products)。首頁不再顯示這個橫幅;要搬回首頁需要改程式碼,不只是打開開關。',
    defaults: {},
    fields: [],
  },
  magazine: {
    key: 'magazine',
    label: '媒體報導精選',
    description: '最新一期媒體報導的精選區。',
    Component: MagazineFeature,
    defaults: { slug: '' },
    fields: [
      {
        kind: 'select',
        key: 'slug',
        label: '指定哪一期',
        options: [
          { value: '', label: '自動（FEATURED_MAGAZINE）' },
          ...MAGAZINES.map((m) => ({
            value: m.slug,
            label: `${m.issue} · ${m.publication}`,
          })),
        ],
        help: '留空＝沿用 FEATURED_MAGAZINE 的寫法。',
      },
    ],
  },
  lectures: {
    key: 'lectures',
    label: '講座紀錄',
    description: '最新講座紀錄的卡片列表（與 /lectures 同步）。',
    Component: LecturesFeature,
    defaults: { limit: 3, audience: '' },
    fields: [
      {
        kind: 'number',
        key: 'limit',
        label: '顯示幾篇',
        min: 1,
        max: 12,
        step: 1,
        help: '原本固定取 3 篇。',
      },
      {
        kind: 'select',
        key: 'audience',
        label: '對象過濾',
        options: [
          { value: '', label: '全部' },
          { value: 'teacher', label: '教師場' },
          { value: 'parent', label: '家長場' },
          { value: 'student', label: '學生場' },
        ],
        // The audience select mirrors the admin's existing audience filter
        // on /admin/cms/lectures, so an operator already knows what these
        // values mean. Empty value = no filter (today's behaviour).
        help: '留空＝不過濾（顯示全部）。',
      },
    ],
  },
  news: {
    key: 'news',
    label: '最新文章',
    description: '最新發佈文章的卡片列表。',
    Component: NewsTeaser,
    defaults: { limit: 3 },
    fields: [
      {
        kind: 'number',
        key: 'limit',
        label: '顯示幾篇',
        min: 1,
        max: 12,
        step: 1,
        help: '原本固定取 3 篇。',
      },
    ],
  },
  testimonials: {
    key: 'testimonials',
    label: '學校回饋',
    description: '「來自學校的真實回饋」輪播。',
    Component: Testimonials,
    defaults: {},
    fields: [],
    note: '2026-09-16 按要求從首頁隱藏。元件與文案都還在，開啟此開關即可復原。',
  },
  team: {
    key: 'team',
    label: '團隊介紹',
    description: '團隊成員卡片。',
    Component: TeamGrid,
    defaults: {},
    fields: [],
    note: '元件已寫好但從未接入首頁序位，預設關閉。',
  },
}

/** Every key the registry knows about, in declaration order. Used by the admin
 *  to show which implemented sections have no row yet. */
export const KNOWN_SECTION_KEYS: string[] = Object.keys(SECTION_REGISTRY)

/**
 * Merge stored settings over the defaults, dropping anything that does not
 * match the declared field type.
 *
 * This is the guard that matters: settings_json is operator-editable JSON
 * arriving from a build-time generated file, and a bad value must degrade to
 * the default rather than crash or, worse, render a section with nonsense.
 */
export function coerceSettings(def: SectionDef, raw: Record<string, unknown> | undefined): SectionSettings {
  const out: SectionSettings = { ...def.defaults }
  if (!raw) return out
  for (const field of def.fields) {
    const v = raw[field.key]
    if (v === undefined || v === null) continue
    switch (field.kind) {
      case 'number': {
        const n = typeof v === 'number' ? v : Number(v)
        if (Number.isFinite(n)) {
          out[field.key] = Math.min(field.max, Math.max(field.min, Math.round(n)))
        }
        break
      }
      case 'toggle': {
        if (typeof v === 'boolean') out[field.key] = v
        break
      }
      case 'select': {
        if (typeof v === 'string' && field.options.some((o) => o.value === v)) out[field.key] = v
        break
      }
      case 'multiselect': {
        if (Array.isArray(v)) {
          const allowed = new Set(field.options.map((o) => o.value))
          out[field.key] = v.filter((x): x is string => typeof x === 'string' && allowed.has(x))
        }
        break
      }
    }
  }
  return out
}

/**
 * Turn the generated section list into the ordered, visible, renderable set.
 * Unknown keys are dropped with a warning — see rule 1 in the file header.
 *
 * ORDERING: the CMS's `sort_order` is authoritative.
 *
 * The operator drags sections in the admin, and the drag writes a new
 * sort_order for every row (PUT /api/cms/admin/sections/reorder). That is
 * the ONLY place the homepage's order is decided, so the resolver has to
 * honour it — otherwise the admin shows one order and the homepage renders
 * another, and the operator's drag silently does nothing.
 *
 * This used to sort by the REGISTRY's declaration order instead, on the
 * assumption that "the server already returns rows sorted by sort_order, so
 * in practice this sort is a no-op". That assumption held only while the
 * registry order happened to match the CMS order. Adding a section to the
 * registry in a different position than its sort_order broke it: the
 * homepage rendered the registry order while the admin showed the CMS one.
 *
 * The registry order is now only the TIEBREAKER, for rows that somehow
 * share a sort_order (a hand-edited row, say) — it still yields a
 * deterministic order rather than an arbitrary one.
 */
export function resolveHomeSections(sections: CmsHomeSection[]): Array<{
  key: string
  def: SectionDef
  settings: SectionSettings
}> {
  const out: Array<{
    key: string
    def: SectionDef
    settings: SectionSettings
    sortOrder: number
  }> = []
  for (const s of sections) {
    const def = SECTION_REGISTRY[s.section_key]
    if (!def) {
      console.warn(
        `[home-sections] CMS lists section "${s.section_key}" but no component is registered for it — skipped.`,
      )
      continue
    }
    // Registry can mark a section as code-disabled (e.g. AboutSnippet while
    // it iterates). The CMS row is still skipped; the admin still sees the
    // entry via SECTION_REGISTRY, but the homepage renders nothing for it.
    if (def.enabled === false) continue
    out.push({
      key: def.key,
      def,
      settings: coerceSettings(def, s.settings),
      sortOrder: Number.isFinite(s.sort_order) ? s.sort_order : Number.MAX_SAFE_INTEGER,
    })
  }
  return out
    .sort((a, b) => a.sortOrder - b.sortOrder || orderOf(a.key) - orderOf(b.key))
    .map(({ key, def, settings }) => ({ key, def, settings }))
}

/** Fallback ordering: the registry's own declaration order, i.e. the sequence
 *  the homepage shipped with. */
function orderOf(key: string): number {
  const i = KNOWN_SECTION_KEYS.indexOf(key)
  return i === -1 ? Number.MAX_SAFE_INTEGER : i
}